import { useAuthCompanyInvitationSignup } from "@boundless/bl-api-client/auth/services"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { toast } from "sonner"

import { FormCheckbox } from "@/components/form-elements/FormCheckbox"
import { FormConsentText } from "@/components/form-elements/FormConsentText"
import { FormInput } from "@/components/form-elements/FormInput"
import { FormInputPhone } from "@/components/form-elements/FormInputPhone"
import { Button, LoadingButton } from "@/components/ui/button"
import { Form } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import type { ErrorType } from "@/lib/clients"
import { applyErrorsToForm, type ErrorResponseType } from "@/lib/forms/utils"
import { companyInvitationFormSchema } from "@/lib/schemas"

import type { CompanyInvitationToken } from "@boundless/bl-api-client/auth/models"
import type { z } from "zod"

interface Props {
  invitation: CompanyInvitationToken
  token: string
  onSuccess: () => void
}

/** Creates the account behind an invited address and joins it to the company. */
export function CompanyInvitationForm({ invitation, token, onSuccess }: Props) {
  const form = useForm<z.infer<typeof companyInvitationFormSchema>>({
    resolver: zodResolver(companyInvitationFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      password: "",
      passwordConfirm: "",
      hasAcceptedTerms: false,
    },
  })

  const { mutate: signup, isPending } = useAuthCompanyInvitationSignup({
    mutation: {
      onSuccess: () => {
        onSuccess()
      },
      onError: (error) => {
        if (error.status === 400) {
          applyErrorsToForm(
            form.setError,
            error as ErrorType<ErrorResponseType>,
          )
        } else {
          toast.error(error.message)
        }
      },
    },
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit((data) => signup({ token, data }))}>
        <div className="flex flex-col space-y-4">
          <Input placeholder={invitation.email} disabled />

          <FormInput name="firstName" placeholder="First name" />
          <FormInput name="lastName" placeholder="Last name" />
          <FormInputPhone name="phone" placeholder="Phone" />
          <FormInput
            name="password"
            placeholder="Password"
            type="password"
            autoComplete="new-password"
          />
          <FormInput
            name="passwordConfirm"
            placeholder="Confirm password"
            type="password"
            autoComplete="new-password"
          />

          <div className="px-8 py-2">
            <FormCheckbox
              name="hasAcceptedTerms"
              className="size-6 self-center"
              label={<FormConsentText action="checking this box" />}
            />
          </div>

          {isPending ? (
            <LoadingButton />
          ) : (
            <Button type="submit">Join {invitation.companyName}</Button>
          )}
        </div>
      </form>
    </Form>
  )
}
